import React, {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    useGetCheckoutQuestionsQuery, useGetStorefrontQuery, useLazyGetOrderPaymentUrlQuery, useSetOrderQuestionsMutation
} from '../Api/Api';
import {
    Button, Field, Dropdown, Option, Textarea, MessageBar, MessageBarBody
} from '@fluentui/react-components';
import useStyles from '../styles';
import FullPageSpinner from '../Components/Spinner/FullPageSpinner';
import {IOrderQuestionSource} from "../Api/Models/StorefrontSettings";
import {IOrderQuestion} from "../Api/Models/Order";
import { parseCheckoutPageConfig } from '../Components/Helpers/checkoutPageConfig';
import { elementStyleFrom } from '../Components/Helpers/searchPageConfig';
import { useLocalization } from '../Components/Helpers/useLocalization';


const OrderPrerequisitesPage: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const L = useLocalization();
    const [orderPaymentUrl] = useLazyGetOrderPaymentUrlQuery();
    const questions = useGetCheckoutQuestionsQuery({orderId: orderId ?? ''}, {skip: !orderId || orderId === ''});
    const styles = useStyles();
    const navigate = useNavigate();
    const storefrontSettings = useGetStorefrontQuery();
    // Merchant checkout-page styling (checkoutPageConfig JSON). Each section applies to a bounding
    // component; plain boxes/headings/buttons are applied inline.
    const checkoutConfig = React.useMemo(() => parseCheckoutPageConfig(storefrontSettings.data?.checkoutPageConfig), [storefrontSettings.data?.checkoutPageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.container), [checkoutConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.title, 'heading'), [checkoutConfig.title]);
    const prerequisitesBoxStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.prerequisitesBox), [checkoutConfig.prerequisitesBox]);
    const continueButtonStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.continueButton), [checkoutConfig.continueButton]);
    const [answers, setAnswers] = React.useState<string[]>([]);
    const [orderQuestions, setOrderQuestions] = React.useState<IOrderQuestionSource[]>([]);
    const [questionsSubmissions] = useSetOrderQuestionsMutation();
    const [submitting, setSubmitting] = React.useState(false);
    const [warningMessage, setWarningMessage] = React.useState<string>('');
    // Check if all required questions are answered

    // When submitting, scroll to the top so the full-page loading spinner is visible
    useEffect(() => {
        if (submitting) {
            window.scrollTo({ top: 0, left: 0 });
        }
    }, [submitting]);

    useEffect(() => {
        if(questions.data) {
            if (questions.data?.questions && questions.data.questions.length > 0) {
                setOrderQuestions(questions.data.questions);
                const emptyAnswers: string[] = new Array(questions.data.questions.length).fill('');
                setAnswers(emptyAnswers)
            }
            else if(orderId) {
                orderPaymentUrl({orderId: orderId}).unwrap().then(resp => {
                    if (resp.paymentUrl) {
                        window.location.href = resp.paymentUrl;
                    } else {
                        navigate(`/checkout/${orderId}`);
                    }
                }).catch(() => navigate(`/checkout/${orderId}`))
            }
        }
    }, [questions.data, orderId, navigate, orderPaymentUrl]);
    
    const allRequiredAnswered = () => {
        return orderQuestions.every((q, i) => {
            if (!q.isRequired) return true;
            const answer = answers[i];
            if (q.allowMultiSelect) {
                // For multi-select, check for non-empty string
                return !!answer && answer.trim() !== '';
            }
            return !!answer && answer.trim() !== '';
        });
    }
    const getAnswers = () => {
        const mergedAnswers: IOrderQuestion[] = []
        answers.forEach((a, i) => {
            const correspondingQuestion = orderQuestions[i];
            mergedAnswers.push({
                identifier: correspondingQuestion.identifier ?? '',
                entityType: correspondingQuestion.entityType ?? '',
                category: correspondingQuestion.category,
                entityId: correspondingQuestion.entityId ?? '',
                answer: a,
                questionText: correspondingQuestion.questionText
            })
        })
        return mergedAnswers;
    }
    const orderPrerequisites = async () => {
        if(orderId) {
            setSubmitting(true);
            const {data, error} = await questionsSubmissions({
                orderId: orderId,
                questions: getAnswers()
            });

            if (error) {
                console.log('got to error handling with object error:', error);
                const typedError = error as { data?: { success: boolean; error?: string }};
                setWarningMessage(typedError?.data?.error || L('localization_order_questions_error'));
                setSubmitting(false);
                return;
            }
            
            if(!data?.success) {
                setWarningMessage(data?.error ?? L('localization_order_questions_error'));
                setSubmitting(false);
                return;
            }

            orderPaymentUrl({orderId: orderId}).unwrap().then(resp => {
                if (resp.paymentUrl) {
                    window.location.href = resp.paymentUrl;
                } else {
                    navigate(`/checkout/${orderId}`);
                }
            }).finally(() => {
                setSubmitting(false);
            })
        }
    }
    
    if (questions.isLoading) return <FullPageSpinner message={L('localization_order_verifying_requirements')} />
    if (submitting) return <FullPageSpinner message={L('localization_order_submitting_prerequisites')} />
    // No prerequisite questions → the effect above is redirecting to payment/checkout. Show the spinner
    // during that hand-off instead of briefly flashing an empty prerequisites box + Continue button.
    if (questions.data && !(questions.data.questions && questions.data.questions.length > 0))
        return <FullPageSpinner message={L('localization_order_verifying_requirements')} />
    return (
        <div className={styles.mainContent} style={{maxWidth: '600px', margin: '2em auto', alignContent: 'center', justifyContent: 'center', alignItems: 'center', ...containerStyle}}>
            <span><h1 style={titleStyle}>{L('localization_order_prerequisites_title')}</h1></span>
            <div className='order-prerequisites-box' style={{border: '2px solid #ccc', backgroundColor: '#FFF', borderRadius: '8px', padding: '2em', maxWidth: '600px', margin: '2em auto', ...prerequisitesBoxStyle}}>
                <div style={{marginTop: '1em'}}>
                    {orderQuestions.map((question, index) => {
                        const selectedAnswer = answers[index];
                        const handleChange = (value: string, index: number) => {
                            const newAnswers = [...answers];
                            newAnswers[index] = value;
                            setAnswers([...newAnswers]);
                        };
                        if (question.answers && question.answers.length > 0) {
                            if (question.allowMultiSelect) {
                                // Multi-select combo box
                                return (
                                    <Field key={`Question-${index}`} label={question.questionText} required={question.isRequired}>
                                        <Dropdown
                                            multiselect={true}
                                            selectedOptions={selectedAnswer ? selectedAnswer.split(", ") : []}
                                            onOptionSelect={(_, data) => {
                                                const combinedAnswer = data.selectedOptions.join(", ")
                                                handleChange(combinedAnswer, index);
                                            }}
    
                                            style={{ width: '100%' }}
                                        >
                                            {question.answers.map((ans, i) => (
                                                <Option key={i} value={ans}>{ans}</Option>
                                            ))}
                                        </Dropdown>
                                    </Field>
                                );
                            } else {
                                // Single-select dropdown
                                return (
                                    <Field key={`Question-${index}`} label={question.questionText} required={question.isRequired}>
                                        <Dropdown
                                            value={selectedAnswer || ''}
                                            onOptionSelect={(ev, data) => {
                                                handleChange(data.optionValue ?? '', index)
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            {question.answers.map((ans, i) => (
                                                <Option key={i} value={ans}>{ans}</Option>
                                            ))}
                                        </Dropdown>
                                    </Field>
                                );
                            }
                        } else {
                            // Free text input
                            return (
                                <Field key={`Question-${index}`} label={question.questionText} required={question.isRequired}>
                                    <Textarea
                                        required={question.isRequired}
                                        value={selectedAnswer || ''}
                                        onChange={e => handleChange(e.target.value, index)}
                                        style={{ width: '100%' }}
                                    />
                                </Field>
                            );
                        }
                    })}
                    <div style={{paddingTop: '1em'}}>
                        <Button appearance='primary' style={{width: '100%', ...continueButtonStyle}}
                                disabled={!allRequiredAnswered() || submitting}
                                onClick={orderPrerequisites}>{L('localization_order_continue_to_checkout')}</Button>
                    </div>
                </div>
                {warningMessage &&
                    <MessageBar intent={"warning"}>
                        <MessageBarBody>
                            {warningMessage}
                        </MessageBarBody>
                    </MessageBar>
                }
            </div>
        </div>
    )
}

export default OrderPrerequisitesPage;